import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOptionsGroup, OnyxTableData } from '@onyx/angular';
import {
  AmountHelper,
  OnyxInformationHeadingComponent,
  OnyxModalService,
  OnyxTableComponent,
  OnyxToastService,
} from '@onyx/angular';
import { chain } from 'lodash';
import { getOrderModalPaymentHistoryColumns } from '../../../../../common/constants/order-modal-payments-history-columns';
import { OrderStatus } from '../../../../../common/enums/order-status';
import type { Order } from '../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../common/services/orders.service';
import type { OrderPaymentHistoryItemData } from '../order-modal-payment-form-container/order-modal-payment-form-container.component';
import type { OrderPaymentEditModalData } from './order-modal-payment-edit/order-modal-payment-edit.component';
import { OrderModalPaymentEditComponent } from './order-modal-payment-edit/order-modal-payment-edit.component';

@Component({
  selector: 'app-order-modal-payments-history',
  imports: [OnyxTableComponent, OnyxInformationHeadingComponent, TranslatePipe],
  templateUrl: './order-modal-payments-history.component.html',
  styleUrl: './order-modal-payments-history.component.scss',
})
export class OrderModalPaymentsHistoryComponent {
  private readonly ordersService = inject(OrdersService);
  private readonly toastService = inject(OnyxToastService);
  private readonly modalService = inject(OnyxModalService);

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.orderModal';

  protected columns = computed(() =>
    getOrderModalPaymentHistoryColumns(
      this.order().status.value === OrderStatus.PAID ? 'history' : 'current',
    ),
  );
  protected paymentsHistoryData = computed<OrderPaymentHistoryItemData[]>(
    () => {
      const payment = this.order().payment;
      const { net, vat } = payment.invoice!;

      return chain(payment.history)
        .reverse()
        .orderBy((item) => item.date)
        .reduce((result: OrderPaymentHistoryItemData[], item) => {
          const remainingNet = result.at(0)?.remainingNet ?? net;
          const remainingVat = result.at(0)?.remainingVat ?? vat;

          return [
            {
              ...item,
              netPercentage: item.net ? item.net.value / net.value : null,
              remainingNet: item.net
                ? AmountHelper.subtract(remainingNet, item.net)
                : remainingNet,
              vatPercentage: item.vat ? item.vat.value / vat.value : null,
              remainingVat: item.vat
                ? AmountHelper.subtract(remainingVat, item.vat)
                : remainingVat,
            },
            ...result,
          ];
        }, [])
        .value();
    },
  );

  protected getPaymentOptions = (
    data: OnyxTableData<OrderPaymentHistoryItemData>,
  ): OnyxOptionsGroup<() => void>[] => {
    return [
      {
        id: '0.default',
        options: [
          {
            name: 'labels.edit',
            value: () =>
              this.modalService.open<OrderPaymentEditModalData>(
                OrderModalPaymentEditComponent,
                { order: this.order(), data: data.item },
              ),
            leftIcon: { name: 'edit-outlined', size: 20 },
            leftIconColor: 'gray',
          },
          {
            name: 'labels.delete',
            value: () => this.deletePayment(data.item.uuid),
            leftIcon: { name: 'delete-outlined', size: 20 },
            leftIconColor: 'red',
          },
        ],
      },
    ];
  };

  private deletePayment(uuid: string) {
    this.ordersService.deletePayment(this.order().uuid, uuid).subscribe(() =>
      this.toastService.showSuccess({
        message: `${this.I18N}.toasts.paymentDeleted`,
        keepOnNavigation: true,
      }),
    );
  }
}
